/**
 * Phase 2G.7 - daily cash reconciliation & handover accountability.
 *
 * A reconciliation compares RECORDED collections (the receiving payment -
 * paymentMethod + amountPaidCents, integer cents, dated by the moment it
 * was recorded) against ACTUAL amounts a preparer physically counted or
 * verified for one branch and one Asia/Manila business day.
 *
 * Honest boundaries of this model:
 *   - a recorded payment does NOT prove cash was physically received;
 *     the actual side is what a person counted/verified and attests to,
 *   - there is no payment-event ledger, so recorded collections for a
 *     business day are the receiving timestamps of that Manila day
 *     (the same basis Phase 2G.6 reports - never fabricated),
 *   - CASH is a physical count, GCASH/MAYA are preparer-verified amounts
 *     (no external account verification exists), OTHER stays ambiguous
 *     and is never silently classified as cash,
 *   - a recorded payment with NO method cannot be matched to any actual
 *     count and is disclosed as unmatched, never dropped,
 *   - null actual = "not counted yet"; differences are null until every
 *     method is counted - a missing count is never treated as zero.
 *
 * Everything here is pure: statuses, labels, completeness and signed
 * difference calculations shared by API responses and UI previews.
 */

import { PAYMENT_METHODS, type PaymentMethod } from './schemas.js';

/** Reconciliation lifecycle: DRAFT -> SUBMITTED -> REVIEWED (forward only). */
export const RECONCILIATION_STATUSES = ['DRAFT', 'SUBMITTED', 'REVIEWED'] as const;
export type ReconciliationStatus = (typeof RECONCILIATION_STATUSES)[number];

export const RECONCILIATION_STATUS_LABELS: Record<ReconciliationStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  REVIEWED: 'Reviewed',
};

/** Fields carrying the preparer's actual figures (null = not counted yet). */
export const RECONCILIATION_ACTUAL_FIELDS = {
  CASH: 'cashCountedCents',
  GCASH: 'gcashVerifiedCents',
  MAYA: 'mayaVerifiedCents',
  OTHER: 'otherActualCents',
} as const satisfies Record<PaymentMethod, string>;

export type ReconciliationActualField =
  (typeof RECONCILIATION_ACTUAL_FIELDS)[PaymentMethod];

/** The four nullable actual-count fields of one reconciliation. */
export interface ReconciliationActuals {
  cashCountedCents: number | null;
  gcashVerifiedCents: number | null;
  mayaVerifiedCents: number | null;
  otherActualCents: number | null;
}

export const EMPTY_ACTUALS: ReconciliationActuals = {
  cashCountedCents: null,
  gcashVerifiedCents: null,
  mayaVerifiedCents: null,
  otherActualCents: null,
};

/** What each method's actual figure MEANS (never blur them together). */
export const ACTUAL_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Cash counted',
  GCASH: 'GCash verified',
  MAYA: 'Maya verified',
  OTHER: 'Other (as entered)',
};

export const ACTUAL_METHOD_HINTS: Record<PaymentMethod, string> = {
  CASH: 'Physical cash count performed by the preparer',
  GCASH: 'Amount verified by the preparer (no external account check exists)',
  MAYA: 'Amount verified by the preparer (no external account check exists)',
  OTHER: 'Entered as-is; the underlying method is ambiguous and not counted as cash',
};

/** Actual figure for one method (null = not counted yet). */
export function actualForMethod(
  actuals: ReconciliationActuals,
  method: PaymentMethod,
): number | null {
  return actuals[RECONCILIATION_ACTUAL_FIELDS[method]];
}

/** Methods whose actual figure has not been provided yet (submission blocker). */
export function missingActualMethods(actuals: ReconciliationActuals): PaymentMethod[] {
  return PAYMENT_METHODS.filter((m) => actualForMethod(actuals, m) === null);
}

/** True when every method has an actual figure (counted, possibly zero). */
export function isComplete(actuals: ReconciliationActuals): boolean {
  return missingActualMethods(actuals).length === 0;
}

/** Sum of the provided actual figures; null while any method is missing. */
export function actualTotalCents(actuals: ReconciliationActuals): number | null {
  if (!isComplete(actuals)) return null;
  return PAYMENT_METHODS.reduce((sum, m) => sum + (actualForMethod(actuals, m) ?? 0), 0);
}

/**
 * Signed difference for one method: recorded - actual.
 *  negative -> the counted/verified amount EXCEEDS what was recorded,
 *  zero     -> matches, positive -> recorded exceeds the counted amount.
 * Null when the actual figure has not been counted yet (never coerced to 0).
 */
export function differenceCents(recordedCents: number, actualCents: number | null): number | null {
  if (actualCents === null) return null;
  return recordedCents - actualCents;
}

/** Total signed difference; null while the actual figures are incomplete. */
export function totalDifferenceCents(
  recordedTotalCents: number,
  actuals: ReconciliationActuals,
): number | null {
  const actual = actualTotalCents(actuals);
  if (actual === null) return null;
  return recordedTotalCents - actual;
}
